import type { OfflineKitStatus } from '@/services/offline-kit.api';

/**
 * How old the last download may get before it is stale regardless of changes.
 * Quiet periods are exactly when nobody thinks to refresh the kit, and the
 * floor keeps the habit alive through them.
 */
export const STALE_AFTER_DAYS = 90;

export type OfflineKitStaleness =
  | { level: 'none' }
  /** Flows changed since the last download. Low emphasis, dismissible. */
  | { level: 'info'; changedFlows: number }
  /** No kit at all, or one past the floor. Not dismissible. */
  | { level: 'warning'; reason: 'never' | 'old'; ageDays?: number; changedFlows: number };

const DAY_MS = 86_400_000;

/**
 * Whether the admin's offline copy is out of date, and how loudly to say so.
 *
 * A statement about the world right now, not a reaction to events: it reads
 * only how many flows differ from the last download, which the server counts
 * per flow. So it can never get louder because someone published a lot.
 */
export function offlineKitStaleness(status: OfflineKitStatus, now: Date): OfflineKitStaleness {
  const { lastExport, changes, preflight } = status;

  if (!lastExport) {
    // Nothing published means nothing to download, so nothing to nag about.
    if (preflight.flows.length === 0) return { level: 'none' };
    return { level: 'warning', reason: 'never', changedFlows: 0 };
  }

  const changedFlows = changes ? changes.updated + changes.added + changes.removed : 0;
  const ageDays = Math.floor((now.getTime() - new Date(lastExport.at).getTime()) / DAY_MS);

  if (ageDays > STALE_AFTER_DAYS) return { level: 'warning', reason: 'old', ageDays, changedFlows };
  if (changedFlows > 0) return { level: 'info', changedFlows };
  return { level: 'none' };
}

/**
 * Session key for a dismissed informational banner.
 *
 * Tied to the last download, not to the change count: dismissing "flows have
 * changed since your last download" stays dismissed however much more gets
 * published, and only a new download starts the question over.
 */
export function dismissKey(status: OfflineKitStatus): string {
  return `offline-kit-banner-dismissed:${status.lastExport?.downloadId ?? 'never'}`;
}
